import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { trackSignup, trackSignupComplete, setEnhancedConversionsIdentity } from '../utils/analytics';
import { getAttribution, identify } from '../utils/attribution';
import { fireSessionExpiry, onSessionExpiry } from '../utils/authEvents';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Cookie helpers ─────────────────────────────────────────────
function getCsrfToken() {
  const match = document.cookie.match(/(?:^|;\s*)cpdforge_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

// ─── Global axios config ────────────────────────────────────────
// withCredentials ensures HttpOnly cookies are sent on every request.
axios.defaults.withCredentials = true;

// Attach CSRF token to every mutation request.
axios.interceptors.request.use((config) => {
  const method = (config.method || '').toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrf = getCsrfToken();
    if (csrf) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  // Backward compat: if a Bearer token exists in state, attach it.
  // This covers API clients and the brief migration period.
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    // Only set if not already present (some components set it explicitly)
    if (!config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Keep localStorage token as fallback during migration.
  // New logins set HttpOnly cookies as primary auth.
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const interceptorRef = useRef(null);

  // ─── SESSION POLICY ────────────────────────────────────────────
  // Network interruption must not be treated as authentication failure.
  // Only confirmed invalid/expired/revoked tokens should clear session state.
  // This applies to: axios interceptor, validateToken, generation polling,
  // and any future authenticated request handler.
  // ───────────────────────────────────────────────────────────────

  // Global axios interceptor: auto-logout only for genuine token death (expired/invalid),
  // not for transient 401s from internal server errors.
  useEffect(() => {
    interceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && (token || document.cookie.includes('cpdforge_session'))) {
          const detail = error.response?.data?.detail || '';
          // Only fire session expiry for definitive token death signals
          if (detail === 'Token expired' || detail === 'Invalid token' || detail === 'Not authenticated') {
            fireSessionExpiry('axios-interceptor');
          }
          // For generic 'Unauthorized' or other 401s: do not kill the session —
          // let the individual component handle the error and retry if needed.
        }
        return Promise.reject(error);
      }
    );
    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
      }
    };
  }, [token]);

  // Listen for session-expiry events
  useEffect(() => {
    const cleanup = onSessionExpiry(({ source }) => {
      console.debug(`[AuthContext] handling session-expiry from: ${source}`);
      localStorage.removeItem('token');
      localStorage.removeItem('mfa_setup_mode');
      setToken(null);
      setUser(null);
      localStorage.removeItem('learner_token');
      // Store the expired-session flag for the login page to pick up
      sessionStorage.setItem('session_expired', '1');
      // Do NOT use window.location.href — that causes a full page reload
      // and a homepage "flash". Instead, clearing token/user above triggers
      // React route guards to redirect to login naturally within the SPA.
    });
    return cleanup;
  }, []);

  useEffect(() => {
    if (token || document.cookie.includes('cpdforge_csrf')) {
      validateToken();
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateToken = async () => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 1500;
    // Clear any stale session_expired flag from previous polling errors —
    // we're actively validating now, so don't let an old flag redirect us.
    sessionStorage.removeItem('session_expired');

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`);
        setUser(response.data);
        if (!token && response.data) {
          setToken('cookie-auth');
        }
        setLoading(false);
        return; // Success
      } catch (error) {
        const status = error.response?.status;
        const detail = error.response?.data?.detail || '';

        // Definitive auth death — clear session immediately, no retry
        if (status === 401 && (
          detail === 'Token expired' ||
          detail === 'Invalid token' ||
          detail === 'Not authenticated' ||
          detail === 'Session invalidated. Please sign in again.' ||
          detail === 'User not found'
        )) {
          console.debug(`[AuthContext] Token invalid (${detail}) — clearing session`);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          setLoading(false);
          return;
        }

        // Transient error (network/500) — retry if attempts remain
        if (attempt < MAX_RETRIES) {
          console.debug(`[AuthContext] validateToken attempt ${attempt + 1} failed (${status || 'network'}) — retrying in ${RETRY_DELAY_MS}ms`);
          await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
          continue;
        }

        // All retries exhausted — keep token for next page load
        console.debug(`[AuthContext] validateToken failed after ${MAX_RETRIES + 1} attempts — server unreachable`);
      }
    }
    // All retries exhausted. Token preserved, but user is null → ProtectedRoute will redirect.
    // On next load, validateToken will try again with the preserved token.
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    });
    const data = response.data;

    // P1 / Item 3: MFA challenge — caller must finish via verifyMfa().
    if (data?.mfa_required) {
      return {
        mfaRequired: true,
        mfaPendingToken: data.mfa_pending_token,
        expiresInSeconds: data.expires_in_seconds || 300,
      };
    }

    // MFA enforcement: privileged user without MFA must set up first.
    if (data?.mfa_setup_required) {
      const setupToken = data.mfa_setup_token;
      if (setupToken) {
        localStorage.setItem('token', setupToken);
        setToken(setupToken);
      }
      return {
        mfaSetupRequired: true,
        mfaSetupToken: setupToken,
        message: data.message,
        setupPath: data.setup_path || '/admin/mfa',
      };
    }

    // Cookies are set by the response automatically.
    // Also store token in localStorage for backward compat during migration.
    const { token: newToken, user: userData } = data;
    if (newToken) {
      localStorage.setItem('token', newToken);
      setToken(newToken);
    }
    setUser(userData);
    try { identify(newToken); } catch { /* ignore */ }
    try { setEnhancedConversionsIdentity(email); } catch { /* ignore */ }
    return userData;
  };

  const verifyMfa = async (mfaPendingToken, code, isRecoveryCode = false, trustDevice = false) => {
    const { data } = await axios.post(`${API_URL}/api/auth/mfa/verify`, {
      mfa_pending_token: mfaPendingToken,
      code,
      is_recovery_code: isRecoveryCode,
      trust_device: trustDevice,
    });
    const { token: newToken, user: userData } = data;
    if (newToken) {
      localStorage.setItem('token', newToken);
      setToken(newToken);
    }
    setUser(userData);
    return userData;
  };

  const register = async (emailOrPayload, password, workspace_type = 'organisation', planContext = {}) => {
    let payload;
    
    if (typeof emailOrPayload === 'object' && emailOrPayload.email) {
      payload = emailOrPayload;
    } else {
      payload = {
        email: emailOrPayload,
        password,
        workspace_type,
      };
      if (planContext.plan_id) {
        payload.trial_plan = planContext.plan_id;
      }
      if (planContext.skip_trial) {
        payload.skip_trial = planContext.skip_trial;
      }
    }
    
    if (!payload.attribution) {
      try { payload.attribution = getAttribution(); } catch { /* ignore */ }
    }
    const response = await axios.post(`${API_URL}/api/auth/register`, payload);
    const { token: newToken, user: userData } = response.data;
    if (newToken) {
      localStorage.setItem('token', newToken);
      setToken(newToken);
    }
    setUser(userData);
    try { identify(newToken); } catch { /* ignore */ }
    trackSignup({ method: 'email', ...planContext });
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('intent') === 'trial' ? 'trial_modal' : 'direct';
    // Enhanced Conversions: set hashed-email identity before firing the signup conversion.
    try { await setEnhancedConversionsIdentity(payload.email); } catch { /* ignore */ }
    trackSignupComplete({ signup_source: source, method: 'email', plan_id: planContext.plan_id || payload.trial_plan });
    return userData;
  };

  const logout = async () => {
    // Call server-side logout to clear cookies
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (_) { /* best effort */ }
    localStorage.removeItem('token');
    localStorage.removeItem('mfa_setup_mode');
    localStorage.removeItem('mfa_readiness_dismissed');
    setToken(null);
    setUser(null);
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
    return response.data;
  };

  const resetPasswordWithToken = async (token, newPassword) => {
    const response = await axios.post(`${API_URL}/api/auth/reset-password-confirm`, {
      token,
      new_password: newPassword
    });
    return response.data;
  };

  const changePassword = async (oldPassword, newPassword) => {
    const response = await axios.put(`${API_URL}/api/auth/change-password`, {
      old_password: oldPassword,
      new_password: newPassword
    });
    return response.data;
  };

  const refreshUser = async () => {
    if (token || document.cookie.includes('cpdforge_csrf')) await validateToken();
  };

  return (
    <AuthContext.Provider value={{ user, token, login, verifyMfa, register, logout, loading, refreshUser, forgotPassword, resetPasswordWithToken, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
